import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Users, QrCode, Truck, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { auth } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    // Check if already authenticated
    auth.me()
      .then(() => {
        navigate('/', { replace: true });
      })
      .catch(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await auth.login(formData.email, formData.password);
        toast.success('Login effettuato!');
      } else {
        await auth.register(formData.email, formData.password, formData.name);
        toast.success('Account creato con successo!');
      }
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1E3D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1E3D] to-[#1E40AF] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img 
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Stemma_dell%27Arma_dei_Carabinieri.svg/1200px-Stemma_dell%27Arma_dei_Carabinieri.svg.png"
            alt="ANC Logo"
            className="h-24 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white mb-2">Campo Protezione Civile</h1>
          <p className="text-blue-200">Associazione Nazionale Carabinieri</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">
              {isLogin ? "Accedi al Sistema" : "Crea Account Amministratore"}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? "Inserisci le tue credenziali" 
                : "Il primo utente diventa amministratore"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Mario Rossi"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    data-testid="input-name"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@esempio.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={isLogin ? "••••••••" : "Minimo 8 caratteri"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit"
                className="w-full h-12 text-base bg-[#0B1E3D] hover:bg-[#1E40AF]"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {isLogin ? "Accesso in corso..." : "Creazione account..."}
                  </>
                ) : (
                  isLogin ? "Accedi" : "Crea Account"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#0B1E3D] hover:underline"
                data-testid="toggle-mode-btn"
              >
                {isLogin 
                  ? "Primo accesso? Crea account amministratore" 
                  : "Hai già un account? Accedi"}
              </button>
            </div>

            <div className="pt-4 border-t mt-4">
              <p className="text-xs text-center text-slate-500 mb-4">
                Sistema di gestione per operazioni di Protezione Civile
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Users className="h-4 w-4 text-[#0B1E3D]" />
                  <span>Anagrafica</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <QrCode className="h-4 w-4 text-[#DC2626]" />
                  <span>Badge QR</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Truck className="h-4 w-4 text-[#0B1E3D]" />
                  <span>Veicoli</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Shield className="h-4 w-4 text-[#10B981]" />
                  <span>GDPR</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-blue-200 mt-6">
          &copy; {new Date().getFullYear()} ANC - Protezione Civile
        </p>
      </div>
    </div>
  );
}
